<script setup lang="ts">
import {API_URL, fetchJson, getTimezone, isEmpty, isInsideIFrame} from "../ts/utils";
import {computed, ref} from "vue";
import PasswordBasedLogin from "../components/PasswordBasedLogin.vue";
import {
  $t,
  ELO_LOCALES,
  getDefaultLocale,
  getSelectedLocale,
  loadMessages,
  pickLocaleFrom,
  setLocale
} from "../ts/translations";
import LocaleSelector from "../components/LocaleSelector.vue";

if(isInsideIFrame()) {
  // If this page is displayed within an IFrame, open the sign-in page in a popup for security reasons
  // An external website might for example add a key listener to the iframe content
  const error = 'Authentication within an IFrame is forbidden for security reasons. Please use `?display=popup` to trigger authentication in a popup.'
  window.alert(error)
  throw error
}

async function fetchRef(url :string) {
  const json = await fetchJson(url)
  return ref(json)
}

const timezone = ref(getTimezone());

// // EAUTH2-49: language choice comes from server and influenced by "ui_locales" parameter
const signInChoices = await fetchRef(API_URL + '/sign-in-choices');
let currentLocale = getSelectedLocale();
const ui_locales = signInChoices.value.ui_locales ?? ELO_LOCALES;
if(ui_locales &&  ui_locales.length > 0 && !ui_locales.includes(currentLocale)) {
  console.log(`Selecting another locale due to \`ui_locales\` constraint: ${ui_locales}`)
  const newLocale = pickLocaleFrom(ui_locales)
  console.log(`Locale switching: ${currentLocale} => ${newLocale}`)
  await setLocale(newLocale);
}
else {
  await loadMessages();
}


// EAUTH2-197: when coming from a bookmark, there is no session available.
// Therefore, the `client_id` is unknown from the server and will be retrieved from the URL parameters.
// Otherwise, the call will be skipped and the session parameters will be the "source of truth".
if(!signInChoices.value.client_id && window.location.search) {
  const res = await fetch(`${API_URL}/revalidate-params${window.location.search}`)
  if(res.ok) {
    console.log('Validated search parameters successfully')
  }
  else {
    console.error('Invalid URL parameters', res)
    window.alert('Invalid URL parameters')
  }
}


const selectedLocale = ref(getSelectedLocale())

const waitForResponse = ref(false)

if (signInChoices.value.provider) {
  console.log(`Signing in using provider "${signInChoices.value.provider}`)
  window.location.href = `${API_URL}/sign-in?provider=${encodeURIComponent(signInChoices.value.provider)}&timezone=${encodeURIComponent(timezone.value)}`
}

if (signInChoices.value.ixSsoEnabled && !signInChoices.value.enablePasswordLogin && isEmpty(signInChoices.value.providerLabels)) {
  // no other choice than legacy SSO? => trigger it directly
  await loginIxSSO()
}

const errorMessageSSO = ref('')

const hasProviders = computed(() => {
  return !isEmpty(signInChoices.value.providerLabels) || signInChoices.value.ixSsoEnabled
})



function signInWith(providerId :string) {
  window.location.href = `${API_URL}/sign-in?provider=${encodeURIComponent(providerId)}&timezone=${encodeURIComponent(timezone.value)}`;
}

async function loginIxSSO() {
  // see EAUTH2-16
  console.log("Signing in using legacy SSO")

  waitForResponse.value = true
  const res = await fetch('../../bypsauth/auth?streamversion=9.00.000.000&responseformat=byps-json&language=' + getSelectedLocale())
  waitForResponse.value = false

  if(res.ok) {
    console.log("Signed in with SSO successfully")
    // EAUTH2-192: initialize session options also after legacy ix SSO
    const initSessionRes = await fetch(`${API_URL}/init-session`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json', // Part of EAUTH2-192 but added in EAUTH2-199
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        locale: getSelectedLocale(),
        timezone: getTimezone()
      })
    })
    if(!initSessionRes.ok) {
      console.warn('Failed to initialize session options after SSO')
      console.warn(await res.text())
      // continue login nevertheless with default locale and timezone
    }

    // To resume the redirection flow to the client (possibly code exchange flow)
    window.location.href = API_URL + "/check-roles"
  }
  else {
    console.warn('Failed to sign-in with SSO')
    window.alert(await res.text())
  }
}

/**
 * Continue sign-in flow after successful password-based login.
 */
async function onLoginWithPassword() {
  console.log("Continue sign-in flow...")
  // To resume the redirection flow to the client (possibly code exchange flow)
  window.location.href = API_URL + "/check-roles"
}
</script>



<template>
  <b-loading is-full-page v-model="waitForResponse"></b-loading>
  <section v-if="hasProviders">
    <div class="bar-with-text">
      <hr/>
      <p>{{$t('sign_in_with')}}</p>
      <hr/>
    </div>
    <template v-for="pl in signInChoices.providerLabels">
      <b-button expanded type="is-light" class="my-4" @click="signInWith(pl.id)" :data-test-id="'button-sign_in_with_' + pl.id">{{pl.label}}</b-button>
    </template>

    <template v-if="signInChoices.ixSsoEnabled">
      <b-button type="is-light" class="my-4" expanded @click="loginIxSSO()" data-test-id="button-sign_in_legacy_sso">
        {{signInChoices.ixSsoButtonLabel || $t('sign_in_sso') || $t('sign_in')}}
      </b-button>
      <div class="error-message" v-if="errorMessageSSO">
        <b-icon icon="notification-error" class="mx-1"></b-icon>
        <span data-test-id="error-message-sso">{{errorMessageSSO}}</span>
      </div>
    </template>
  </section>

  <template v-if="hasProviders && signInChoices.enablePasswordLogin">
    <section class="bar-with-text">
      <hr/>
      <p>{{$t('or')}}</p>
      <hr/>
    </section>
  </template>

  <section v-if="signInChoices.enablePasswordLogin">
    <PasswordBasedLogin :username="signInChoices.login_hint" @success="onLoginWithPassword()"></PasswordBasedLogin>

    <!-- This is an old WF feature, not widely known, mostly used in the original "Teamspace". Ask @Nesbigall for more details. -->
    <template v-if="signInChoices.hasOldRegistration || signInChoices.hasOldRecovery">
      <p class="old-wf-links">
        <a v-if="signInChoices.legacyWfFeatures.hasRecovery"     :href="'../de.elo.ix.plugin.proxy/wf/apps/app/elo.webapps.Login/?mode=init_recover&lang=' + getSelectedLocale()"  class="has-text-grey">{{$t('recover')}}</a>
        <a v-if="signInChoices.legacyWfFeatures.hasRegistration" :href="'../de.elo.ix.plugin.proxy/wf/apps/app/elo.webapps.Login/?mode=init_register&lang=' + getSelectedLocale()" class="has-text-grey">{{$t('register')}}</a>
      </p>
    </template>
  </section>

  <div class="has-text-right" v-if="ui_locales.length > 1">
    <locale-selector v-model="selectedLocale" :ui_locales="signInChoices.ui_locales"/>
  </div>
</template>




<style scoped>
.bar-with-text {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bar-with-text > hr {
  flex-grow: 1;
  margin: 0;
  background-color: #CCCCCC;
  height: 1px;
}


.old-wf-links {
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
}

.old-wf-links > a:only-child {
  margin: auto; /* centered if single link */
}

</style>
